import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { BehaviorSubject } from 'rxjs';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';

/**
 * Modal for adding one or more Azure AD users to ESRA. Mirrors the member-search pattern from
 * `create-release-wizard.component` -- search, add as a chip, remove, then submit every selected
 * id in a single `addUser` call. Closes with `true` when users were added so the list page knows
 * to reload.
 */
@Component({
    selector: 'app-add-user-dialog',
    templateUrl: './add-user-dialog.component.html',
    styleUrls: ['./add-user-dialog.component.scss'],
    standalone: false
})
export class AddUserDialogComponent {
  private readonly esraApiClient = inject(EsraPipingAPIClient);
  private readonly snackBarService = inject(SnackBarService);
  private readonly insightsService = inject(InsightsService);
  private readonly dialogRef = inject(MatDialogRef<AddUserDialogComponent, boolean>);
  private readonly destroyRef = inject(DestroyRef);

  selectedUsers: MicrosoftGraph.User[] = [];

  readonly saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  addUser(user: MicrosoftGraph.User): void {
    const alreadyAdded: boolean = this.selectedUsers.some((selected: MicrosoftGraph.User) => selected.id === user.id);
    if (alreadyAdded) {
      this.snackBarService.showError(`${user.displayName} has already been added.`);
      return;
    }
    this.selectedUsers.push(user);
  }

  removeUser(user: MicrosoftGraph.User): void {
    this.selectedUsers = this.selectedUsers.filter((selected: MicrosoftGraph.User) => selected.id !== user.id);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (this.selectedUsers.length === 0) {
      return;
    }

    const ids: string[] = this.selectedUsers.map((user: MicrosoftGraph.User) => user.id);

    this.esraApiClient.addUser(ids).pipe(
      indicate(this.saving$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.snackBarService.showInfo(this.selectedUsers.length === 1 ? 'User added.' : `${this.selectedUsers.length} users added.`);
        this.dialogRef.close(true);
      },
      error: (err: unknown) => {
        this.snackBarService.showError(getApiErrorMessage(err, 'Failed to add user(s).'));
        this.insightsService.logException(err as Error);
      },
    });
  }
}
